import { gql } from "@apollo/client";
import { toNumber } from "lodash-es";
import { avg } from "../common/utils";
import Municipality from "../data-models/municipality";
import School from "../data-models/school";
import { BaseUser } from "../data-models/user";
import { graphQlClient } from "./graphql";
import { RoleService } from "./roleService";

export const SchoolService = {
  GET_SCHOOL: gql`
    query School($schoolId: Int!) {
      school(id: $schoolId) {
        name
        municipality {
          name
          county
        }
        contact {
          name
          email
          phone
          address
          zip
          city
        }
      }
    }
  `,

  UPDATE_SCHOOL: gql`
    mutation School(
      $schoolId: Int!
      $name: String!
      $email: String!
      $phone: String!
      $address: String!
      $zip: String!
      $city: String!
    ) {
      updateSchoolContact(
        schoolId: $schoolId
        contact: { name: $name, email: $email, phone: $phone, address: $address, zip: $zip, city: $city }
      )
    }
  `,

  GET_MUNICIPALITY_SCHOOLS_BASIC: gql`
    query Schools($municipalityId: Int!) {
      schools(municipalityId: $municipalityId) {
        id
        name
        contact {
          email
          phone
        }
      }
    }
  `,

  GET_AVAILABLE_SCHOOLS_BASIC: gql`
    query SchoolsByIds($schoolIds: [Int!]) {
      schoolsByIds(ids: $schoolIds) {
        id
        name
        contact {
          email
          phone
        }
      }
    }
  `,

  GET_SCHOOLS_STATISTICS: gql`
    query SchoolStatistics(
      $semesters: [String!]
      $programCode: [String!]
      $schoolIds: [Int!]
      $programType: [String!]
    ) {
      schoolStatistics(
        semesters: $semesters
        programCode: $programCode
        schoolIds: $schoolIds
        programType: $programType
      ) {
        id
        semester
        school {
          id
          name
        }
        programCode
        programType
        values {
          value
          name
        }
      }
    }
  `,

  GET_THROUGHPUT_SANKEY: gql`
    query AbsencesSankey($schoolIds: [Int!], $semester: String!, $programCodes: [String!]) {
      absencesSankey(schoolIds: $schoolIds, semester: $semester, programCodes: $programCodes) {
        flow2
        flow3
        flow4
        school1 {
          id
        }
        programCode1
        studentId
      }
    }
  `,

  GET_ABSENCES_STATISTICS: gql`
    query absenceImpactSummary(
      $schoolIds: [Int!]
      $programCodes: [String!]
      $grades: [Int!]
      $gender: [String!]
      $semester: String
      $municipalityId: Int!
    ) {
      absenceImpactSummary(
        schoolIds: $schoolIds
        programCodes: $programCodes
        grades: $grades
        gender: $gender
        semester: $semester
        municipalityId: $municipalityId
      ) {
        gender
        school {
          id
          name
        }
        grade
        totalGraduatingTrue
        totalGraduatingFalse
        highAbsenceCount
        lowAbsenceCount
        highAbsenceGraduatingCount
        lowAbsenceGraduatingCount
        programCode
        totalAbsenceLengthGrantedLeave
        totalAbsenceLengthMotivated
        totalAbsenceLengthUnmotivated
        totalStudents
        totalAbsencePercentageGrantedLeave
        totalAbsencePercentageMotivated
        totalAbsencePercentageUnMotivated
        highAbsenceExamGraduatingCount
        lowAbsenceExamGraduatingCount
      }
    }
  `,

  GET_ABSENCE_COUNT: gql`
    query AbsenceGraduationRelations(
      $schoolIds: [Int!]
      $semesters: [String]
      $programCode: [String]
      $programType: [String]
    ) {
      absenceGraduationRelations(
        schoolIds: $schoolIds
        semesters: $semesters
        programCode: $programCode
        programType: $programType
      ) {
        highAbsenceExamGraduatedCount
        highAbsenceGraduatedCount
        lowAbsenceExamGraduatedCount
        lowAbsenceGraduatedCount
        school {
          name
        }
        programDetails {
          programCode
          programName
          programType
        }
      }
    }
  `,

  GET_ABSENCE_COUNT_NEW: gql`
    query AbsenceGraduationRelations(
      $schoolIds: [Int!]
      $semesters: [String]
      $programCode: [String]
      $programType: [String]
    ) {
      absenceGraduationRelations(
        schoolIds: $schoolIds
        semesters: $semesters
        programCode: $programCode
        programType: $programType
      ) {
        highAbsenceExamGraduatedCount
        highAbsenceGraduatedCount
        lowAbsenceExamGraduatedCount
        lowAbsenceGraduatedCount
        highAbsenceExamGraduatedCountFemale
        highAbsenceGraduatedCountFemale
        lowAbsenceExamGraduatedCountFemale
        lowAbsenceGraduatedCountFemale
        highAbsenceExamGraduatedCountMale
        highAbsenceGraduatedCountMale
        lowAbsenceExamGraduatedCountMale
        lowAbsenceGraduatedCountMale
        school {
          name
        }
        programDetails {
          programCode
          programName
          programType
        }
      }
    }
  `,

  GET_EFTER_GRUNDSKOLA: gql`
    query MeritGraduationRelations(
      $schoolIds: [Int!]
      $semesters: [String]
      $programCode: [String]
      $programType: [String]
    ) {
      meritGraduationRelations(
        schoolIds: $schoolIds
        semesters: $semesters
        programCode: $programCode
        programType: $programType
      ) {
        graduatingCount80To120
        graduatingCount120To160
        graduatingCount160To205
        graduatingCount205To249
        graduatingCount249To294
        graduatingCount294Above
        graduatingExamCount80To120
        graduatingExamCount120To160
        graduatingExamCount160To205
        graduatingExamCount205To249
        graduatingExamCount249To294
        graduatingExamCount294Above
        school {
          name
        }
        programDetails {
          programCode
          programName
          programType
        }
      }
    }
  `,

  GET_PROGRAM_SCATTER_PLOT: gql`
    query GetProgramScatterPlots($municipalityId: Int!) {
      programScatterPlots(municipalityId: $municipalityId) {
        programType
        year
        values {
          programScatterPlotId
          parentsWithHigherEducation
          foreignBackground
          gBPGeneral
          merit
        }
      }
    }
  `,

  GET_SHAPLEY: gql`
    query GetShapleys($municipalityId: Int!) {
      shapleys(municipalityId: $municipalityId) {
        feature
        diagram
        filter
        shapleyValues {
          featureValue
          shapleyValueScore
        }
      }
    }
  `,

  GET_LINEDATA_MERIT: gql`
    query LineData($municipalityId: Int!) {
      lineData(municipalityId: $municipalityId) {
        feature
        category
        values {
          xValue
          yValue
        }
      }
    }
  `,
  GET_ABSENCE_HEATMAP: gql`
    query AbsenceBlocks(
      $year: Int!
      $municipalityId: Int!
      $gender: String
      $programShortCode: String
      $schoolIds: [Int!]
      $subject: String
    ) {
      absenceBlocks(
        year: $year
        municipalityId: $municipalityId
        gender: $gender
        programShortCode: $programShortCode
        schoolIds: $schoolIds
        subject: $subject
      ) {
        dayOfWeek
        blockTime
        isAbsence
      }
    }
  `,
  GET_METRICS_OVERVIEW: gql`
    query DashboardGridSummary($schoolIds: [Int!], $semester: String, $programCodes: [String], $municipalityId: Int!) {
      dashboardGridSummary(
        schoolIds: $schoolIds
        semester: $semester
        programCodes: $programCodes
        municipalityId: $municipalityId
      ) {
        absenceTotalPercentageSchool
        dropoutCount
        studentPerTeacher
        totalStudents
        teacherWithCollegeDegree
        finishedGrade3
        gymnasieexamen
        school {
          name
        }
        programDetails {
          programCode
          programName
          programType
        }
      }
    }
  `,

  GET_SCHOOL_DATA_OVER_TIME: gql`
    query SchoolDataOverTime(
      $schoolIds: [Int!]
      $semesters: [String]
      $programName: [String]
      $programType: [String]
      $municipalityIds: [Int!]
    ) {
      schoolDataOverTime(
        schoolIds: $schoolIds
        semesters: $semesters
        programName: $programName
        programType: $programType
        municipalityIds: $municipalityIds
      ) {
        semester
        programType
        parentsWithHigherEducation
        foreignBackground
        school {
          name
          id
        }
        merit
        programName
        gbp
        gbpPercentage
        programCode
        programName
        exam
      }
    }
  `,

  GET_GYMNASIUM_PROGRAMS: gql`
    query GymnasiumPrograms($municipalityId: Int!, $schoolIds: [Int!], $semester: String) {
      programs(municipalityId: $municipalityId, schoolIds: $schoolIds, semester: $semester) {
        programName
        programType
        programCode
      }
    }
  `,

  GET_SCHOOL_SUMMARIES: gql`
    query SchoolSummaries($municipalityId: Int!) {
      schoolSummaries(municipalityId: $municipalityId) {
        school {
          id
          name
        }
        absenceTotalPercentage
        preschoolMonths
        preschoolHoursPerWeek
        foreignBackground
        english
        swedish
        mathematics
        semester
        eligibility
        averageFailedCourses
        parentsWithHigherEducation
      }
    }
  `,

  GET_SCHOOLS_SEMESTER_SUMMARY: gql`
    query SchoolsSemesterSummary($municipalityId: Int!) {
      schoolsSemesterSummary(municipalityId: $municipalityId) {
        semester
        municipalityId
        foreignVsPreschoolHoursIntercept
        foreignVsPreschoolHoursSlope
        parentsEducationVsPreschoolHoursIntercept
        parentsEducationVsPreschoolHoursSlope
      }
    }
  `,

  GET_SCHOOL_SUMMARY_GBP: gql`
    query SchoolSummariesGbp($municipalityId: Int!) {
      schoolSummariesGbp(municipalityId: $municipalityId) {
        school {
          id
          name
        }
        semester
        gbpNewlyArrived
        gbpNotNewlyArrived
        foreignBackground
        parentsHigherEducation
        absence
        studentsPerTeacher
        preschoolHoursWeek
        preschoolMonths
      }
    }
  `,

  GET_DASHBOARD_DATA: gql`
    query DashboardGridSummary($municipalityId: Int!, $semester: String!, $schoolIds: [Int!]) {
      dashboardGridSummary(municipalityId: $municipalityId, semester: $semester, schoolIds: $schoolIds) {
        school {
          id
          name
        }
        semester
        results
        schoolEvalutionScore
        schoolHealthMetrics
        socioEconomics
      }
    }
  `,

  GET_BACKGROUND_GBP_DATA: gql`
    query FeaturesImpactGbpBeesworms($municipalityId: Int!) {
      featuresImpactGbpBeesworms(municipalityId: $municipalityId) {
        foreignBackground
        foreignBackgroundImpactGbp
        parentsHigherEducation
        parentsHigherEducationImpactGbp
        absencePercentage
        absencePercentageImpactGbp
        studentsPerTeacher
        studentsPerTeacherImpactGbp
        schoolTempenStudents
        schoolTempenStudentsImpactGbp
        teacherSickLeave
        teacherSickLeaveImpactGbp
        teachersSpecialEducation
        teachersSpecialEducationImpactGbp
        staffTurnover
        staffTurnoverImpactGbp
        teacherQuestionnaire
        teacherQuestionnaireImpactGbp
      }
    }
  `,

  GET_BACKGROUND_ELIG_DATA: gql`
    query FeaturesImpactEligBeesworms($municipalityId: Int!) {
      featuresImpactEligBeesworms(municipalityId: $municipalityId) {
        foreignBackground
        foreignBackgroundImpactElig
        parentsHigherEducation
        parentsHigherEducationImpactElig
        absencePercentage
        absencePercentageImpactElig
        studentsPerTeacher
        studentsPerTeacherImpactElig
        schoolTempenStudents
        schoolTempenStudentsImpactElig
        teacherSickLeave
        teacherSickLeaveImpactElig
        teachersSpecialEducation
        teachersSpecialEducationImpactElig
        staffTurnover
        staffTurnoverImpactElig
        teacherQuestionnaire
        teacherQuestionnaireImpactElig
      }
    }
  `,

  GET_SCHOOL_SUMMARY_GBP_LINES: gql`
    query SchoolSummariesGbpLines($municipalityId: Int!) {
      schoolSummariesGbpLines(municipalityId: $municipalityId) {
        gbpNewlyArrivedVsForeignBackgroundIntercept
        gbpNewlyArrivedVsForeignBackgroundSlope
        gbpNewlyArrivedVsParentsHigherEducationIntercept
        gbpNewlyArrivedVsParentsHigherEducationSlope
        gbpNewlyArrivedVsAbsenceIntercept
        gbpNewlyArrivedVsAbsenceSlope
        gbpNewlyArrivedVsStudentsPerTeacherIntercept
        gbpNewlyArrivedVsStudentsPerTeacherSlope

        gbpNotNewlyArrivedVsForeignBackgroundIntercept
        gbpNotNewlyArrivedVsForeignBackgroundSlope
        gbpNotNewlyArrivedVsParentsHigherEducationIntercept
        gbpNotNewlyArrivedVsParentsHigherEducationSlope
        gbpNotNewlyArrivedVsAbsenceIntercept
        gbpNotNewlyArrivedVsAbsenceSlope
        gbpNotNewlyArrivedVsStudentsPerTeacherIntercept
        gbpNotNewlyArrivedVsStudentsPerTeacherSlope
        gbpNotNewlyArrivedVsPreschoolHoursWeekIntercept
        gbpNotNewlyArrivedVsPreschoolHoursWeekSlope
        gbpNotNewlyArrivedVsPreschoolMonthsIntercept
        gbpNotNewlyArrivedVsPreschoolMonthsSlope
      }
    }
  `,

  getSchoolsForCurrentUser: (user: BaseUser, municipality: Municipality) => {
    let query;
    let variables: any;
    let resultKey: string;

    if (RoleService.hasMunicipalitySchoolsSubset(user)) {
      query = SchoolService.GET_AVAILABLE_SCHOOLS_BASIC;
      variables = { schoolIds: user.schoolIds };
      resultKey = "schoolsByIds";
    } else {
      query = SchoolService.GET_MUNICIPALITY_SCHOOLS_BASIC;
      variables = {
        municipalityId: municipality.code,
      };
      resultKey = "schools";
    }

    return graphQlClient
      .query({
        query,
        variables,
      })
      .then(({ data }) => {
        const arrayForSort = [...data[resultKey]];
        return (arrayForSort.sort((a, b) => a.name!.localeCompare(b.name!)) || []) as Partial<School>[];
      });
  },

  getSchoolStatistic: (school: Partial<School>, statistic: string) =>
    (school.statistics || [])
      .flatMap(s => s.values)
      .filter(s => s.name === statistic)
      .map(s => toNumber(s.value)),

  getSchoolStatisticAvg: (schools: Partial<School>[], statistic: string) =>
    avg(
      schools.flatMap(s => SchoolService.getSchoolStatistic(s, statistic)),
      2,
    ),
};
